/**
 * Collection overlay-toggle factory (view settings, filters, sort).
 *
 * AI: Entirely written by AI
 * Date: 2026-05-05
 * Model: Claude Opus 4.7
 * Updated: 2026-09-26 - Added SORT_NAMESPACE for the sort panel
 *
 * [CollectionOverlay]
 */

import { log } from './log.js';
import { safeAppCall } from './app-get.js';
import { saveLastFocused, restoreLastFocused, bindEscape, unbindEscape } from './overlay.js';

export const VIEW_SETTINGS_NAMESPACE = 'viewSettingsOverlay';
export const FILTERS_NAMESPACE = 'filtersOverlay';
export const SORT_NAMESPACE = 'sortOverlay';

const TOGGLE_OPEN_CLASS = 'toggle--open';

const closeHandlers = new Map();
// Tracks namespace → overlay element so detachEscape can find which
// overlay to unbind. The ESC listener itself is owned by overlay.esm.js
// (bindEscape uses a WeakMap keyed on element).
const escapeOverlays = new Map();

function setOpenState(toggle, overlay, open) {
  toggle.setAttribute('aria-expanded', String(open));
  toggle.classList.toggle(TOGGLE_OPEN_CLASS, open);
  // Both overlays are `display: none` by default in
  // _collection-filters.scss. Setting an empty string here just removes
  // the inline rule and lets the stylesheet keep them hidden, so opening
  // needs an explicit display value to win over the cascade. (jQuery's
  // .toggle(true) did this implicitly; we have to be explicit.)
  overlay.style.display = open ? 'block' : 'none';
}

function detachClickOutside(namespace) {
  const handler = closeHandlers.get(namespace);
  if (!handler) return;
  document.removeEventListener('mousedown', handler, true);
  closeHandlers.delete(namespace);
}

function detachEscape(namespace) {
  const overlay = escapeOverlays.get(namespace);
  if (!overlay) return;
  unbindEscape(overlay);
  escapeOverlays.delete(namespace);
}

function close(toggle, overlay, namespace) {
  setOpenState(toggle, overlay, false);
  detachClickOutside(namespace);
  detachEscape(namespace);
  restoreLastFocused();
}

function attachClickOutside(toggle, overlay, namespace) {
  detachClickOutside(namespace);
  // requestAnimationFrame defers until after the click that opened the
  // overlay finishes bubbling — otherwise the same event closes it.
  requestAnimationFrame(() => {
    const handler = (event) => {
      if (overlay.contains(event.target) || toggle.contains(event.target)) return;
      close(toggle, overlay, namespace);
    };
    closeHandlers.set(namespace, handler);
    document.addEventListener('mousedown', handler, true);
  });
}

function attachEscape(toggle, overlay, namespace) {
  detachEscape(namespace);
  bindEscape(overlay, () => close(toggle, overlay, namespace));
  escapeOverlays.set(namespace, overlay);
}

/**
 * Returns a handler `(toggle) => void` that opens or closes the overlay
 * matching `overlaySelector` (the toggle's next sibling).
 */
export function createOverlayToggleHandler({ overlaySelector, namespace }) {
  return function (toggle) {
    if (!toggle) return;
    const shouldOpen = toggle.getAttribute('aria-expanded') === 'false';
    const overlay = toggle.nextElementSibling && toggle.nextElementSibling.matches(overlaySelector)
      ? toggle.nextElementSibling
      : null;
    if (!overlay) return;

    setOpenState(toggle, overlay, shouldOpen);

    if (shouldOpen) {
      // saveLastFocused takes no argument — app.setLastFocused captures
      // document.activeElement directly.
      saveLastFocused();
      safeAppCall('trapFocus', overlay);
      attachClickOutside(toggle, overlay, namespace);
      attachEscape(toggle, overlay, namespace);
    } else {
      detachClickOutside(namespace);
      detachEscape(namespace);
      restoreLastFocused();
    }
  };
}

export function teardownOverlays() {
  for (const ns of [VIEW_SETTINGS_NAMESPACE, FILTERS_NAMESPACE, SORT_NAMESPACE]) {
    detachClickOutside(ns);
    detachEscape(ns);
  }
}

log('[CollectionOverlay] registered');
